import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Link } from 'found';
import { string, shape, oneOf, number } from 'prop-types';
import { ProgressDeterminate } from 'common/mdc/progress';
import Icon from 'common/mdc/icon';
import { messages as statusMessages, icons, STATUS_OFFLINE, STATUS_ONLINE } from '@sma/components/device/search/status';
import { iconFlag } from '@sma/components/device/header';
import { EQUALS } from 'common/store/search/advanced/constraint/operators';
import { STATUS } from '@sma/components/device/search/advanced';
import { Widget } from 'common/mdc/card';
import S from 'common/service/sanctuary';
import { sum } from 'ramda';
import { selectors } from '@sma/store/widgets/status';
import Wrapper from 'common/mdc/wrappers/wrapper';
import { connect } from 'react-redux';
import { sumOfMeters } from '../sumOfMeters';
import { actions as tagActions } from "@sma/store/tag/tags";

const messages = defineMessages({
  displayName: {
    id: 'widget.status.name',
    defaultMessage: '{name}',
  },
  onlineInPercentage: {
    id: 'widget.status.onlineInPercentage',
    defaultMessage: '{progress}% online',
  },
  statusLoadError: {
    id: 'widgets.status.loadError',
    defaultMessage: 'There was an error while loading the statistics',
  },
  statusNoDataLoaded: {
    id: 'widgets.status.notLoaded',
    defaultMessage: 'Status statistics are not loaded',
  },
  statusLoadingData: {
    id: 'widgets.status.loadingData',
    defaultMessage: 'Loading status statistics',
  },
  status: {
    id: 'widgets.status.status',
    defaultMessage: 'Meter Status',
  },
});

const progress = ({ total, count }) =>
  total && Math.round((count / total) * 100);

const ProgressBlock = ({ name, total, count }) => (
  <div className="progress-bar">
    <div className="left-label">
      <FormattedMessage
        values={{
          name: name,
        }}
        {...messages.displayName}
      />
      {/**<Link
        className="count"
        to={{
          pathname: '/devices',
          params: {
            initialCriteria: [],
          },
        }}
      >*/}&nbsp;({total})
      {/**</Link>*/}
    </div>
    <div className="right-label">
      <FormattedMessage
        values={{
          progress: progress({ total, count }),
        }}
        {...messages.onlineInPercentage}
      />
    </div>
    <ProgressDeterminate progress={progress({ total, count })} />
  </div>
);

ProgressBlock.propTypes = {
  name: string.isRequired,
  total: number.isRequired,
  count: number.isRequired,
};

const WidgetStatus = ({ statusDataMaybe, loadingStatus, error, clearSelectedTagsList }) => (
  <Widget
    loading={loadingStatus}
    title={<FormattedMessage {...messages.status} />}
    subtitle={
      S.maybeToNullable(S.map(
        ({ data }) => (
          <React.Fragment>
            <Link to={{
              pathname: '/devices',
              params: {
                initialCriteria: [],
              },
            }}
            onClick={() => clearSelectedTagsList()}
            >{sum([
              sumOfMeters(data, "online"),
              sumOfMeters(data, "offline")
            ])}
            </Link>
            <Icon 
              className={"md-dark info-icon"} 
              title={'Shows how many meters have successfully sent data to the system in the last 24 hours.'}
            >
              {"info"}
            </Icon>
          </React.Fragment>
        ),
        statusDataMaybe,
      ))
    }
  >
    <Wrapper
      empty={statusDataMaybe.isNothing}
      loading={loadingStatus}
      error={error}
      errorMessage={<FormattedMessage {...messages.statusLoadError} />}
      notFoundMessage={<FormattedMessage {...messages.statusNoDataLoaded} />}
      loadingMessage={<FormattedMessage {...messages.statusLoadingData} />}
    >
      {S.maybeToNullable(S.map(
        ({ data }) => (
          <div className="widget-status">
            <div className="status-devices">
              <div className="online-devices">
                <Icon>{icons[STATUS_ONLINE]}</Icon>
                <Link
                  className="count"
                  to={{
                    pathname: '/devices',
                    params: {
                      initialCriteria: [
                        {
                        property: STATUS,
                        constraints: [
                          {
                            operator: EQUALS,
                            value: STATUS_ONLINE,
                          },
                        ],
                       },
                      ],
                    },
                  }}
                  onClick={() => clearSelectedTagsList()}
                >
                  {`${sumOfMeters(data, "online")} `}
                  <FormattedMessage {...statusMessages[STATUS_ONLINE]} />
               </Link>
              </div>
              <div className="offline-devices">
                <Icon>{icons[STATUS_OFFLINE]}</Icon>
                <Link
                  className="count"
                  to={{
                    pathname: '/devices',
                    params: {
                      initialCriteria: [
                        {
                        property: STATUS,
                        constraints: [
                          {
                            operator: EQUALS,
                            value: STATUS_OFFLINE,
                          },
                         ],
                       },
                     ],
                    },
                  }}
                  onClick={() => clearSelectedTagsList()}
                >
                  {`${sumOfMeters(data, 'offline')} `}
                  <FormattedMessage {...statusMessages[STATUS_OFFLINE]} />
                </Link>
              </div>
            </div>
            {
              data &&
              data.map((category) => (
                sum([category.online, category.offline, category.unknown]) > 0 &&
                <ProgressBlock
                  name={category.name}
                  total={sum([category.online, category.offline, category.unknown])}
                  count={category.online}
                />
              ))
            }
          </div>
        ),
        statusDataMaybe,
      ))}
    </Wrapper>
  </Widget>
);

WidgetStatus.propTypes = {
  statusDataMaybe: shape().isRequired,
};

WidgetStatus.defaultProps = {
  statusDataMaybe: undefined,
};

export default connect(
  state => ({
    statusDataMaybe: selectors.getStatusWidgetMaybe(state),
    error: selectors.getError(state),
    loadingStatus: selectors.isLoading(state),
  }),
  ({
    clearSelectedTagsList: tagActions.clearSelectedTagsList,
  }),
)(WidgetStatus);
